import {
  FAILED_TO_GET_ONBOARDING_STATUS,
  GenericError,
} from '@Src/commons/Constants';
import Spinner from '@Src/commons/Spinner';
import {
  getCompanyId,
  getCookie,
  getJwtToken,
  getPhoneNumber,
  internalRoles,
  isInternalPlatform,
  setAccessibleUIResources,
} from '@Src/commons/Utils/authUtils';
import { getServiceActivationData } from '@Src/components/Onboarding/OnboardingActions';
import { RootState } from '@Src/store';
import {
  getUIResources,
  logoutAction,
  refreshJWTToken,
  updateAccessibleUIResource,
  updateOnboardingStatus,
  updateUserDetails,
} from '@Src/store/reducers/authSlice';
import { Content } from 'antd/lib/layout/layout';
import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import './Layout.less';
import { TypeServiceActivationData } from '@Src/components/Onboarding/OnboardingProps';
import { useRbacHook } from '@smartpaddle/biz-bamboo-v2.hooks.use-rbac-hook';
import { TypeUIResourceData } from '@Src/commons/GlobalTypes';
import SubmitDocument from '@Src/components/Compliance/SubmitDocument';
import { notification } from 'antd';
import { getJwtKeySourceKey } from '@Src/commons/Utils';

const AppPrivateRouterWithSideNav = React.lazy(
  () => import('@Src/containers/AppRouting/AppPrivateRouterWithSideNav')
);
const OnboardingPrivateRouter = React.lazy(
  () => import('@Src/containers/AppRouting/OnboardingPrivateRouter')
);
const PageUserCreation = React.lazy(
  () => import('@Src/pages/PageUserCompanyCreation/PageUserCreation')
);
const PageCompanyCreation = React.lazy(
  () => import('@Src/pages/PageUserCompanyCreation/PageCompanyCreation')
);
const QuickOnboarding = React.lazy(
  () => import('@Src/components/NextBuyOnboarding')
);
const PageInstaInvoice = React.lazy(
  () => import('@Src/pages/PageInstaInvoice')
);
export default function ComponentAppPrivateRouter() {
  const { authToken, userData } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const { encrypt } = useRbacHook();
  const accessControlSecretKey =
    process.env.REACT_APP_ACCESS_CONTROL_SECRET_KEY || '';
  useEffect(() => {
    setLoading(true);
    const cookieJwt = getCookie(getJwtKeySourceKey());
    const jwt = getJwtToken();
    if (cookieJwt === jwt) {
      refreshJWTToken()
        .then((data) => {
          dispatch(updateUserDetails(data));
          getUIResources()
            .then((response: TypeUIResourceData) => {
              const encryptData = encrypt(response, accessControlSecretKey);
              setAccessibleUIResources(encryptData);
              dispatch(updateAccessibleUIResource(encryptData));
            })
            .catch((err) => {
              throw err;
            });
          setLoading(false);
        })
        .catch((error) => {
          // Handle the error appropriately
          setLoading(false);
          console.error(GenericError.ERR_USER_DETAILS_DECODE, error);

          if (error?.code === 401) {
            // localStorage.clear();
            // history.push('/auth/login');
            notification.error({
              message: error?.message,
              description: 'Please logout and login again',
            });
          }
        });
    } else {
      logoutAction();
      notification.error({
        message: 'Please login again',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userData.company_id) {
      getServiceActivationData()
        .then((res: TypeServiceActivationData[]) =>
          dispatch(updateOnboardingStatus(res))
        )
        .catch((err) => {
          console.error(FAILED_TO_GET_ONBOARDING_STATUS, err);
        });
    }
  }, [dispatch, userData.company_id]);

  const hasRmcInternalRole = () => {
    const roles = internalRoles();
    if (roles && roles.length > 0) {
      return roles.includes('rmc_internal');
    }
    return false;
  };

  useEffect(() => {
    if (authToken) {
      if (
        !getPhoneNumber() &&
        userData.company_id?.toString() !== '1' &&
        !isInternalPlatform()
      ) {
        // if phone number does not exist then redirect to admin_details page
        history.push('/app/admin_details');
      } else if (
        !getCompanyId() &&
        userData.company_id?.toString() !== '1' &&
        !isInternalPlatform()
      ) {
        // if company does not exist then redirect to comapny_details page
        history.push('/app/company_details');
      } else if (
        history.location.pathname === '/app/admin_details' ||
        history.location.pathname === '/app/company_details'
      ) {
        // redirect to root page if phone number and comapny exists and still some one enter above path
        history.push('/');
      }
    }
  }, [authToken, history, history.location.pathname]);
  return (
    <div className="one-bizongo-main">
      <Content className="one-bizongo-main-layout">
        <Suspense fallback={<Spinner />}>
          {loading ? (
            <Spinner />
          ) : (
            <Switch>
              <Route path="/app/admin_details" component={PageUserCreation} />
              <Route
                path="/app/company_details"
                component={PageCompanyCreation}
              />
              <Route
                path="/app/onboarding"
                component={OnboardingPrivateRouter}
              />

              <Route
                exact
                path="/app/next-buy/onboarding"
                render={() =>
                  hasRmcInternalRole() ? (
                    <QuickOnboarding />
                  ) : (
                    <Redirect to="/" />
                  )
                }
              />
              <Route
                path="/app/submit-compliance-reports"
                component={SubmitDocument}
              />
              <Route path="/app/invoices/create" component={PageInstaInvoice} />
              <Route path="/app" component={AppPrivateRouterWithSideNav} />
            </Switch>
          )}
        </Suspense>
      </Content>
    </div>
  );
}
